import originIconUrl from "@/assets/origin-icon.png"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { apiClient, getData, resolveApiBaseUrl } from "@/lib/apiClient"
import { useQuery } from "@tanstack/react-query"
import {
	AlertCircle,
	Apple,
	Download as DownloadIcon,
	ExternalLink,
} from "lucide-react"

const formatBytes = (bytes: number) => {
	if (!Number.isFinite(bytes) || bytes <= 0) {
		return "Unknown size"
	}

	const units = ["B", "KB", "MB", "GB"]
	let value = bytes
	let unitIndex = 0

	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024
		unitIndex += 1
	}

	return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`
}

const formatReleaseDate = (releaseDate?: string) => {
	if (!releaseDate) {
		return "Unknown date"
	}

	const parsed = new Date(releaseDate)
	if (Number.isNaN(parsed.getTime())) {
		return "Unknown date"
	}

	return parsed.toLocaleDateString(undefined, {
		month: "long",
		day: "numeric",
		year: "numeric",
	})
}

const getLatestOriginRelease = async () => {
	const response = await apiClient.updates.client["latest.json"].$get()
	const latestRelease = await getData(response)
	const macAsset = latestRelease.platforms.macos

	if (!macAsset) {
		throw new Error("No macOS build is available right now.")
	}

	const downloadUrl = `${resolveApiBaseUrl()}/updates/releases/origin/${encodeURIComponent(latestRelease.version)}/download/macos`

	return {
		version: latestRelease.version,
		releaseDate: latestRelease.releaseDate,
		macAsset,
		downloadUrl,
	}
}

export const Download = () => {
	const latestReleaseQuery = useQuery({
		queryKey: ["updates", "origin", "latest"],
		queryFn: getLatestOriginRelease,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	})

	const minimumSystemVersion =
		latestReleaseQuery.data?.macAsset.minimum_system_version ?? "15.0"

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
			<section className="relative overflow-hidden rounded-2xl border bg-card p-6 shadow-sm md:p-10">
				<div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
				<div className="pointer-events-none absolute -bottom-12 -left-14 h-60 w-60 rounded-full bg-primary/5 blur-3xl" />

				<div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
					<div className="flex flex-col gap-5 sm:flex-row sm:items-center">
						<div className="shrink-0 rounded-2xl border bg-background/95 p-3 shadow-sm">
							<img
								src={originIconUrl}
								alt="Origin app icon"
								className="h-20 w-20 rounded-xl"
							/>
						</div>

						<div className="space-y-3">
							{" "}
							<h2 className="text-3xl font-semibold tracking-tight">
								Download Origin for macOS
							</h2>
							<div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
								<Badge variant="outline">
									<Apple className="size-3.5" />
									macOS {minimumSystemVersion}+
								</Badge>
								{latestReleaseQuery.data && (
									<Badge variant="outline">
										v{latestReleaseQuery.data.version}
									</Badge>
								)}
							</div>
							<p className="max-w-2xl text-muted-foreground">
								Native Oblique desktop app for real-time geometry workflows.
								Install the latest stable release and sign in with your Oblique
								account.
							</p>
						</div>
					</div>

					<div className="w-full max-w-sm space-y-3">
						{latestReleaseQuery.isPending ? (
							<Button className="w-full" size="lg" disabled>
								Fetching latest build...
							</Button>
						) : latestReleaseQuery.isError ? (
							<div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
								<p className="flex items-center gap-2 font-medium">
									<AlertCircle className="size-4" />
									Could not load the latest release.
								</p>
								<Button className="mt-3 w-full" variant="outline" asChild>
									<a
										href={`${resolveApiBaseUrl()}/updates/client/latest.json`}
										target="_blank"
										rel="noreferrer"
									>
										View release metadata
										<ExternalLink className="size-4" />
									</a>
								</Button>
							</div>
						) : (
							<>
								<Button className="w-full" size="lg" asChild>
									<a href={latestReleaseQuery.data.downloadUrl}>
										<DownloadIcon className="size-4" />
										Download for macOS
									</a>
								</Button>
								<p className="text-xs text-muted-foreground">
									{latestReleaseQuery.data.macAsset.filename} (
									{formatBytes(latestReleaseQuery.data.macAsset.size_bytes)}) •
									Released{" "}
									{formatReleaseDate(latestReleaseQuery.data.releaseDate)}
								</p>
							</>
						)}
					</div>
				</div>
			</section>

			<p className="text-center text-sm text-muted-foreground">
				Looking for Windows? Support will be available soon, stay tuned!
			</p>
		</div>
	)
}
